/* Specimen sheet: the live ferrule stage and variant readout on the product page. */
import { FINISHES, DEFAULT_FINISH, addLights, parseGeometry } from '@theme/ferrule-mesh';
import { formatMoney } from '@theme/money-formatting';

const AMBIENT_SPEED = 0.15;
const INTRO_MS = 900;
const DRAG_RESUME_MS = 1200;
const CAMERA_FOV = 28;
const CAMERA_ELEVATION = 0.2;
const MESH_TILT = 0.08;

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

class FerruleSpecimen extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;

    this.root = this.closest('[data-specimen-root]') || document;
    this.stage = this.querySelector('[data-stage]');
    this.canvas = this.querySelector('canvas');
    this.status = this.querySelector('[data-status]');
    this.viewButtons = [...this.querySelectorAll('[data-view-button]')];
    this.panels = [...this.querySelectorAll('[data-view-panel]')];

    this.reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
    this.finish = FINISHES[this.dataset.finish] ? this.dataset.finish : DEFAULT_FINISH;
    this.viewChosen = false;
    this.angle = -0.5;
    this.velocity = 0;
    this.lastDragAt = -Infinity;
    this.visible = true;

    this.viewButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.viewChosen = true;
        this.setView(button.dataset.viewButton);
      }, { signal });
    });

    // Option inputs give instant feedback; the variant update carries price and availability.
    document.addEventListener('change', (event) => this.onOptionChange(event), { signal });
    document.addEventListener('variant:update', (event) => this.onVariantUpdate(event), { signal });

    this.setView(this.dataset.view || 'model');
    if (this.canvas && this.dataset.model) {
      this.bindStage(signal);
      this.boot().catch(() => this.fallback());
    }
  }

  get reducedMotion() {
    return this.reducedMotionQuery.matches;
  }

  setView(view) {
    if (!this.panels.some((panel) => panel.dataset.viewPanel === view)) return;
    this.dataset.view = view;
    this.panels.forEach((panel) => { panel.hidden = panel.dataset.viewPanel !== view; });
    this.viewButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.viewButton === view)));
    if (view === 'photos') requestAnimationFrame(() => dispatchEvent(new Event('resize')));
    if (view === 'model') this.requestRender();
  }

  optionIndex(name) {
    const value = parseInt(this.dataset[name], 10);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  onOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.closest('variant-picker') || !this.root.contains(input)) return;
    const finishIndex = this.optionIndex('finishOption');
    if (finishIndex == null || Number(input.dataset.fieldsetIndex) !== finishIndex - 1) return;
    this.setFinish(input.value);
  }

  onVariantUpdate(event) {
    const variant = event.detail?.resource;
    const productId = event.detail?.data?.productId;
    if (!variant || (productId && productId !== this.dataset.productId)) return;
    const option = (name) => {
      const index = this.optionIndex(name);
      return index ? variant[`option${index}`] : null;
    };
    const finish = option('finishOption');
    if (finish) this.setFinish(finish);
    this.updateReadout({
      finish,
      fit: option('fitOption'),
      pack: option('packOption'),
      price: variant.price,
      available: variant.available,
    });
  }

  updateReadout({ finish, fit, pack, price, available }) {
    const set = (key, value) => {
      if (value == null) return;
      this.root.querySelectorAll(`[data-readout="${key}"]`).forEach((node) => { node.textContent = value; });
    };
    set('finish', finish);
    if (fit) {
      const [head, spec] = fit.split(' — ');
      const [tip, ...club] = head.trim().split(' ');
      set('tip', tip);
      set('club', club.join(' ') || '—');
      set('spec', spec || '—');
      set('fit', fit);
    }
    if (pack) set('pack', pack);
    const count = parseInt(pack, 10) || 1;
    if (typeof price === 'number' && this.dataset.moneyFormat) {
      const unit = formatMoney(Math.round(price / count), this.dataset.moneyFormat, this.dataset.currency || 'USD');
      set('unit-price', unit);
      set('total-price', formatMoney(price, this.dataset.moneyFormat, this.dataset.currency || 'USD'));
    }
    set('count', String(count));
    if (typeof available === 'boolean') {
      set('availability', available ? this.dataset.availableLabel : this.dataset.soldOutLabel);
      this.root.querySelectorAll('[data-readout-state]').forEach((node) => { node.dataset.readoutState = String(available); });
    }
  }

  setFinish(value) {
    if (!FINISHES[value] || value === this.finish) return;
    this.finish = value;
    this.dataset.finish = value;
    this.root.querySelectorAll('[data-readout="finish"]').forEach((node) => { node.textContent = value; });
    if (this.mesh) {
      this.applyFinish();
      if (!this.reducedMotion) this.pulseStart = performance.now();
      this.requestRender();
    }
    this.updateLabel();
  }

  bindStage(signal) {
    this.canvas.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.angle += event.key === 'ArrowLeft' ? -0.18 : 0.18;
      this.velocity = 0;
      this.lastDragAt = performance.now();
      this.requestRender();
    }, { signal });
    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      this.pointer = { id: event.pointerId, x: event.clientX, time: performance.now() };
      this.velocity = 0;
      this.canvas.setPointerCapture(event.pointerId);
    }, { signal });
    this.canvas.addEventListener('pointermove', (event) => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const now = performance.now();
      const delta = (event.clientX - this.pointer.x) * 0.009;
      this.angle += delta;
      this.velocity = delta / Math.max(8, now - this.pointer.time);
      this.pointer.x = event.clientX;
      this.pointer.time = now;
      this.lastDragAt = now;
      this.requestRender();
    }, { signal });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => {
      this.canvas.addEventListener(name, () => {
        if (!this.pointer) return;
        if (performance.now() - this.pointer.time > 80) this.velocity = 0;
        this.pointer = null;
        this.lastDragAt = performance.now();
        this.requestRender();
      }, { signal });
    });
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.fallback();
    }, { signal });
  }

  async boot() {
    this.T = await import(this.dataset.three);
    if (!this.isConnected) return;
    this.renderer = new this.T.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true, powerPreference: 'low-power' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = this.T.SRGBColorSpace;
    this.renderer.toneMapping = this.T.ACESFilmicToneMapping;

    this.scene = new this.T.Scene();
    this.camera = new this.T.PerspectiveCamera(CAMERA_FOV, 1, 0.1, 200);
    addLights(this.T, this.scene);

    const response = await fetch(this.dataset.model, { signal: this.abort.signal });
    if (!response.ok) throw new Error('Model unavailable');
    const geometry = parseGeometry(this.T, await response.arrayBuffer());
    if (!this.renderer) return;
    this.mesh = new this.T.Mesh(geometry, new this.T.MeshPhysicalMaterial({ side: this.T.DoubleSide }));
    this.mesh.rotation.x = MESH_TILT;
    this.applyFinish();
    this.scene.add(this.mesh);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.stage);
    this.stageObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.requestRender();
      else this.stopLoop();
    });
    this.stageObserver.observe(this.stage);
    this.resize();

    this.introStart = this.reducedMotion ? null : performance.now();
    this.dataset.state = 'ready';
    this.updateLabel();
    this.requestRender();
  }

  updateLabel() {
    if (!this.canvas) return;
    this.canvas.setAttribute('aria-label', `${this.dataset.title} ferrule in ${this.finish}. Drag or use arrow keys to rotate.`);
    if (this.status && this.dataset.state === 'ready') {
      this.status.textContent = this.hoverQuery.matches ? this.dataset.readyMessage : this.dataset.touchMessage;
    }
  }

  applyFinish() {
    const finish = FINISHES[this.finish] || FINISHES[DEFAULT_FINISH];
    const material = this.mesh.material;
    material.color.setHex(finish.color);
    material.roughness = finish.roughness;
    material.metalness = finish.metalness;
    material.clearcoat = finish.clearcoat;
    material.clearcoatRoughness = 0.35;
    const transparent = finish.opacity < 0.999;
    material.opacity = finish.opacity;
    material.depthWrite = !transparent;
    if (material.transparent !== transparent) {
      material.transparent = transparent;
      material.needsUpdate = true;
    }
    this.renderer.toneMappingExposure = finish.exposure;
  }

  resize() {
    if (!this.renderer) return;
    const { width, height } = this.stage.getBoundingClientRect();
    if (!width || !height) return;
    this.renderer.setSize(width, height, false);
    const aspect = width / height;
    const tan = Math.tan((CAMERA_FOV * Math.PI) / 360);
    const heightDistance = 2.75 / 0.56 / tan;
    const distance = Math.max(heightDistance, 3.1 / 0.6 / (tan * aspect));
    this.camera.aspect = aspect;
    this.camera.position.set(0, distance * Math.sin(CAMERA_ELEVATION), distance * Math.cos(CAMERA_ELEVATION));
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.requestRender();
  }

  requestRender() {
    this.needsRender = true;
    if (this.frameId || !this.renderer || !this.visible || this.dataset.view !== 'model') return;
    this.frameId = requestAnimationFrame(this.frame);
  }

  stopLoop() {
    cancelAnimationFrame(this.frameId);
    this.frameId = 0;
    this.lastTime = undefined;
  }

  frame = (now) => {
    this.frameId = 0;
    if (!this.renderer || !this.isConnected) return;
    const elapsed = Math.min(64, Math.max(0, now - (this.lastTime ?? now)));
    this.lastTime = now;
    const animating = this.step(now, elapsed);
    if (animating || this.needsRender) {
      this.renderer.render(this.scene, this.camera);
      this.needsRender = false;
    }
    if (animating && this.visible && this.dataset.view === 'model') this.frameId = requestAnimationFrame(this.frame);
    else this.lastTime = undefined;
  };

  step(now, elapsed) {
    if (!this.mesh) return false;
    let animating = false;
    if (!this.pointer && Math.abs(this.velocity) > 0.00002) {
      this.angle += this.velocity * elapsed;
      this.velocity *= Math.exp(-elapsed / 320);
      animating = true;
    } else if (!this.pointer) {
      this.velocity = 0;
    }
    if (!this.reducedMotion) {
      if (!this.pointer && now - this.lastDragAt > DRAG_RESUME_MS) this.angle += (AMBIENT_SPEED * elapsed) / 1000;
      animating = true;
    }

    let scale = 1;
    let spin = 0;
    if (this.introStart != null) {
      const progress = Math.min(1, (now - this.introStart) / INTRO_MS);
      const eased = easeOut(progress);
      scale = 0.86 + 0.14 * eased;
      spin = -1.4 * (1 - eased);
      if (progress >= 1) this.introStart = null;
    }
    if (this.pulseStart != null) {
      const progress = Math.min(1, (now - this.pulseStart) / 420);
      scale *= 1 + 0.035 * Math.sin(progress * Math.PI);
      if (progress >= 1) this.pulseStart = null;
    }
    this.mesh.scale.setScalar(scale);
    this.mesh.rotation.y = this.angle + spin;
    return animating;
  }

  fallback() {
    this.stopLoop();
    this.dataset.state = 'fallback';
    this.renderer?.dispose();
    this.renderer = null;
    this.scene = null;
    if (this.status) this.status.textContent = this.dataset.fallbackMessage;
    if (!this.viewChosen) this.setView('photos');
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
    this.stopLoop();
    this.stageObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.mesh?.geometry.dispose();
    this.mesh?.material.dispose();
    this.mesh = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.scene = null;
  }
}

if (!customElements.get('ferrule-specimen')) customElements.define('ferrule-specimen', FerruleSpecimen);
